import { Activity, Gauge, Plus, Workflow } from "lucide-mui";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, Outlet } from "react-router-dom";
import { PROSPECT_ORDER_TRIGGER, SIGNAL_TRIGGER, api } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { userHasGlobalScope } from "../../auth/scopes.ts";
import DropdownMenu from "../../components/DropdownMenu.tsx";
import NavTabs, { type NavTab } from "../../components/NavTabs.tsx";
import PageHeader from "../../components/PageHeader.tsx";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { useTenant } from "../../contexts/TenantContext.tsx";
import { useWorkflowDataTypes } from "../../hooks/resources.ts";
import { TRIGGER_META, isTriggerType } from "./editor/types.ts";

// An entry of the "New workflow" menu; `to` is tenant-relative.
interface NewWorkflowItem {
  key: string;
  label: string;
  secondary: string | null;
  to: string;
}

/** "New workflow", by when it starts: one entry per data stream a record can
 *  match in, then a placed prospect order. */
function NewWorkflowMenu() {
  const { tenantPath } = useTenant();
  const { data: triggerTypes = [] } = useQuery({
    queryKey: keys.workflowTriggerType.list(),
    queryFn: () => api.getWorkflowTriggerTypes(),
  });
  const { data: dataTypes = [] } = useWorkflowDataTypes();

  const triggerLabel = (slug: string) =>
    isTriggerType(slug)
      ? TRIGGER_META[slug].label
      : (triggerTypes.find((t) => t.slug === slug)?.label ?? slug);
  const items: NewWorkflowItem[] = (
    triggerTypes.length
      ? triggerTypes.filter((t) => isTriggerType(t.slug))
      : [{ slug: SIGNAL_TRIGGER }, { slug: PROSPECT_ORDER_TRIGGER }]
  ).flatMap((t): NewWorkflowItem[] =>
    t.slug === SIGNAL_TRIGGER
      ? dataTypes.map((dt) => ({
          key: `${t.slug}:${dt.slug}`,
          label: `${dt.label} matches`,
          secondary: dt.private ? "Your organization's own records" : null,
          to: `/workflows/new?trigger=${SIGNAL_TRIGGER}&data_type=${encodeURIComponent(dt.slug)}`,
        }))
      : [
          {
            key: t.slug,
            label: t.slug === PROSPECT_ORDER_TRIGGER ? "Prospect order placed" : triggerLabel(t.slug),
            secondary: null,
            to: `/workflows/new?trigger=${t.slug}`,
          },
        ],
  );

  return (
    <DropdownMenu
      label="New workflow"
      buttonProps={{ variant: "contained", startIcon: <Plus /> }}
      align="right"
      minWidth={220}
    >
      <ListSubheader>Starts when…</ListSubheader>
      {items.map((item) => (
        <MenuItem key={item.key} component={RouterLink} to={tenantPath(item.to)}>
          <ListItemText primary={item.label} secondary={item.secondary} />
        </MenuItem>
      ))}
    </DropdownMenu>
  );
}

/**
 * The Workflows section: one header, a tab per page. The workflows
 * themselves; the signal log (every record a signal workflow matched, with
 * its research); and how the signal workflows perform, match to qualified.
 * Decisions on the companies they find are taken in Prospects.
 */
export default function WorkflowsLayout() {
  const { selectedTenant, tenantPath } = useTenant();
  const { user } = useAuth();
  const canWrite = userHasGlobalScope(user, "signal_rules:write");
  const canReadSignals = userHasGlobalScope(user, "signals:read");
  const tabs: NavTab[] = [
    { label: "Workflows", to: tenantPath("/workflows"), icon: <Workflow /> },
    ...(canReadSignals
      ? [
          { label: "Signals", to: tenantPath("/workflows/signals"), icon: <Activity /> },
          { label: "Signal performance", to: tenantPath("/workflows/signals/performance"), icon: <Gauge /> },
        ]
      : []),
  ];
  return (
    <>
      <PageHeader
        title="Workflows"
        description={
          <>
            Automations{selectedTenant ? ` for ${selectedTenant.name}` : ""}: what starts them, the
            steps that follow, and the signals they matched
          </>
        }
        actions={canWrite && <NewWorkflowMenu />}
      />
      <NavTabs tabs={tabs} label="Workflow sections" />
      <Outlet />
    </>
  );
}
